export interface RawBlocksConfig {
  features?: {
    lotteryEnabled?: boolean | string | null;
    telegramBotEnabled?: boolean | string | null;
  } | null;
  purchase?: {
    tomanAmount?: number | string | null;
  } | null;
  telegram?: {
    channel?: string | null;
    miniAppBot?: string | null;
    adminId?: string | number | null;
  } | null;
  miniApp?: {
    baseUrl?: string | null;
  } | null;
}

// The Bitcoin lottery and the Telegram bot are switched off by default; flip the matching
// flags in `config/blocks.json` to bring either feature back online.
const FALLBACK_LOTTERY_ENABLED = false;
const FALLBACK_TELEGRAM_BOT_ENABLED = false;
const FALLBACK_PURCHASE_TOMAN = 100_000;
const FALLBACK_TELEGRAM_CHANNEL = "irancoin";
const FALLBACK_MINI_APP_BOT = "@CryptoChangeIRBot";
const FALLBACK_TELEGRAM_ADMIN_ID = "23661426";
const FALLBACK_MINI_APP_URL = "https://t.me/CryptoChangeIRBot?startapp=ref";

function parsePositiveInteger(value: unknown, fallback: number): number {
  if (typeof value === "number" && Number.isFinite(value) && value > 0) {
    return Math.floor(value);
  }
  if (typeof value === "string") {
    const trimmed = value.trim();
    if (!trimmed) {
      return fallback;
    }
    const parsed = Number(trimmed);
    if (Number.isFinite(parsed) && parsed > 0) {
      return Math.floor(parsed);
    }
  }
  return fallback;
}

function parseBoolean(value: unknown, fallback: boolean): boolean {
  if (typeof value === "boolean") {
    return value;
  }
  if (typeof value === "string") {
    const trimmed = value.trim().toLowerCase();
    if (trimmed === "true" || trimmed === "1" || trimmed === "yes") {
      return true;
    }
    if (trimmed === "false" || trimmed === "0" || trimmed === "no") {
      return false;
    }
  }
  return fallback;
}

function parseString(value: unknown, fallback: string): string {
  if (typeof value === "string") {
    const trimmed = value.trim();
    if (trimmed) {
      return trimmed;
    }
  }
  if (typeof value === "number" && Number.isFinite(value)) {
    const normalised = String(Math.trunc(value));
    if (normalised) {
      return normalised;
    }
  }
  return fallback;
}

export interface ComputedBlocksConfig {
  BLOCKS_LOTTERY_ENABLED: boolean;
  BLOCKS_TELEGRAM_BOT_ENABLED: boolean;
  BLOCKS_PURCHASE_TOMAN: number;
  BLOCKS_PURCHASE_RIAL: number;
  BLOCKS_TELEGRAM_CHANNEL: string;
  BLOCKS_TELEGRAM_MINI_APP_BOT: string;
  BLOCKS_TELEGRAM_ADMIN_ID: string;
  BLOCKS_MINI_APP_URL: string;
}

export interface BlocksFeatureOverrides {
  lotteryEnabled?: boolean | string | null;
  telegramBotEnabled?: boolean | string | null;
}

/**
 * Resolves a feature flag from an environment override first, then `config/blocks.json`,
 * then the built-in fallback. Blank overrides are treated as "not set" so an empty Netlify
 * environment variable does not accidentally force a feature off.
 */
function pickFeatureFlag(override: unknown, configured: unknown, fallback: boolean): boolean {
  const configuredValue = parseBoolean(configured, fallback);
  if (override == null || (typeof override === "string" && !override.trim())) {
    return configuredValue;
  }
  return parseBoolean(override, configuredValue);
}

export function buildBlocksConfig(
  rawConfig: RawBlocksConfig | undefined | null,
  overrides?: BlocksFeatureOverrides | null,
): ComputedBlocksConfig {
  const lotteryEnabled = pickFeatureFlag(
    overrides?.lotteryEnabled,
    rawConfig?.features?.lotteryEnabled,
    FALLBACK_LOTTERY_ENABLED,
  );

  const telegramBotEnabled = pickFeatureFlag(
    overrides?.telegramBotEnabled,
    rawConfig?.features?.telegramBotEnabled,
    FALLBACK_TELEGRAM_BOT_ENABLED,
  );

  const purchaseToman = parsePositiveInteger(
    rawConfig?.purchase?.tomanAmount,
    FALLBACK_PURCHASE_TOMAN,
  );

  const telegramChannel = parseString(
    rawConfig?.telegram?.channel,
    FALLBACK_TELEGRAM_CHANNEL,
  );

  const telegramMiniAppBot = parseString(
    rawConfig?.telegram?.miniAppBot,
    FALLBACK_MINI_APP_BOT,
  );

  const telegramAdminId = parseString(
    rawConfig?.telegram?.adminId,
    FALLBACK_TELEGRAM_ADMIN_ID,
  );

  const miniAppUrl = parseString(
    rawConfig?.miniApp?.baseUrl,
    FALLBACK_MINI_APP_URL,
  );

  return {
    BLOCKS_LOTTERY_ENABLED: lotteryEnabled,
    BLOCKS_TELEGRAM_BOT_ENABLED: telegramBotEnabled,
    BLOCKS_PURCHASE_TOMAN: purchaseToman,
    BLOCKS_PURCHASE_RIAL: purchaseToman * 10,
    BLOCKS_TELEGRAM_CHANNEL: telegramChannel,
    BLOCKS_TELEGRAM_MINI_APP_BOT: telegramMiniAppBot,
    BLOCKS_TELEGRAM_ADMIN_ID: telegramAdminId,
    BLOCKS_MINI_APP_URL: miniAppUrl,
  };
}
